import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cloneDeep, has, isEmpty, isEqual } from "lodash";
import { useMutation } from "@apollo/react-hooks";
import { useNavigate } from 'react-router-dom';
import EmailValidator from "email-validator";

import useForm, { FieldList, OnFieldChange, OnReset, SetFormFunction, ValidateFunction } from "core/hooks/useForm";
import useToast from "core/hooks/useToast";
import { SUBMIT_EVENT_REGISTRATION_MUTATION } from "core/includes/queries";
import useCart from "pages/Components/CartPage/hooks/useCart";
import { useAppState } from "core/contexts/AppContext";

const useEventRegistrationForm = ({
    formFields = [],
    allFormFields = [],
    ignoreFormFieldCallback = (f: FormField) => false,
    registrationPageData,
    userCode = '',
    fields = { additionalRegistrants: {} },
    editExistingWithId = null,
    initialRegistrantsData = {},
    confirmTo = '',
} : {
    formFields: Array<FormField>,
    allFormFields?: Array<FormField>,
    ignoreFormFieldCallback: (field: FormField) => boolean,
    registrationPageData: EventRegistrationPage,
    userCode: string|null,
    fields?: any,
    editExistingWithId?: string|null,
    initialRegistrantsData?: any,
    confirmTo?: string,
}): {
    registrationFields: FieldList,
    registrationErrors: FieldList,
    formFieldsForRender: Array<FormField|Array<FormField>>,
    getFormFieldsForRender: (
        formFields: Array<FormField>,
        fields: any,
        { innerData }: { innerData: InnerData }
    ) => Array<FormField>,
    onFieldChange: OnFieldChange,
    addToCart: (eventPage: EventPage, quantity: number, registrationId: string, userCode: string) => any,
    onSubmit: (expandedIds?: Array<number>) => any,
    onReset: OnReset,
    validate: ValidateFunction,
    setFormErrors: SetFormFunction,
    clearFormField: (name: string, { innerData }?: { innerData: InnerData }) => void,
    isValid: boolean,
    isFormChanged: boolean|undefined,
    isSubmitting: boolean,
} => {
    const navigate = useNavigate();
    const { updateCart } = useCart();
    const [, errorToast] = useToast();
    const [submitEventRegistration] = useMutation(SUBMIT_EVENT_REGISTRATION_MUTATION);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const prevRegistrantsData = useRef(undefined);
    const [appState] = useAppState();

    const getIsTextInput = (type: FormFieldVariant) => (
        ['email', 'text', 'number'].includes(type)
    );

    const getIsFormFieldActivated = useCallback((field: any, fields: any) => {
        const parentField = allFormFields.find((ff: any) => ff.id === field.formFieldId);
        const parentFieldType = parentField?.type;
        const parentFieldId = parentField?.id;

        if (!parentFieldId) return false;

        switch(true) {
            case (parentFieldType === 'dropdown' || parentFieldType === 'radio'):
                return field.activateWithValue === fields[parentFieldId]?.value;
            case (parentFieldType === 'checkbox'):
                return (
                    (fields[parentFieldId] && (field.activateWithValue === 'true' || field.activateWithValue === '1')) ||
                    (!fields[parentFieldId] && (field.activateWithValue === 'false' || field.activateWithValue === '0'))
                );
            case (parentFieldType === 'checkbox-multiple'):
                return fields[parentFieldId]?.some((op: any) => op.code === field.activateWithValue);
            default:
                return field.activateWithValue === fields[parentFieldId];
        }
    }, [allFormFields]);

    const createFormFieldsForRender = useCallback((
        formFieldsToMutate: Array<FormField|Array<FormField>>,
        currentFormFieldGroup: Array<FormField>,
        formFields: Array<FormField>,
        fields: any,
        areSub: boolean = false,
    ) => {
        const filteredFormFields = formFields.filter((f: FormField) => f);

        filteredFormFields.forEach((field: FormField, index: number) => {
            const ignoreSub = areSub && (
                !getIsFormFieldActivated(field, fields)
            );

            if (ignoreFormFieldCallback(field) || ignoreSub) return;

            if (!getIsTextInput(field.type)) {
                if (!isEmpty(currentFormFieldGroup)) {
                    formFieldsToMutate.push(cloneDeep(currentFormFieldGroup));
                    currentFormFieldGroup.length = 0;
                }

                formFieldsToMutate.push(field);
            } else {
                currentFormFieldGroup.push(field);
            }

            if (field.subFormFields?.length) {
                createFormFieldsForRender(
                    formFieldsToMutate,
                    currentFormFieldGroup,
                    field.subFormFields,
                    fields,
                    true
                );
            }

            if (!areSub) {
                if (index === (filteredFormFields.length - 1) && !isEmpty(currentFormFieldGroup)) {
                    formFieldsToMutate.push(currentFormFieldGroup);
                }
            }
        });
    }, [ignoreFormFieldCallback, getIsFormFieldActivated]);

    const getFormFieldsForRender = useCallback((formFields: Array<FormField>, fields: any) => {
        const formFieldsForRender: Array<FormField> = [];
        let currentFormFieldGroup: Array<FormField> = [];

        createFormFieldsForRender(formFieldsForRender, currentFormFieldGroup, formFields, fields);

        return formFieldsForRender;
    }, [createFormFieldsForRender]);

    const validateFields = useCallback((
        fields: FieldList,
        errors: any,
        isSubmit: boolean = false,
        { isSecondaryRegistrant = false }: { isSecondaryRegistrant?: boolean } = {},
    ) => {
        let _errors = cloneDeep(errors);
        const isMandatoryCheck = isSecondaryRegistrant ?
            'isMandatoryForSecondaryRegistrant' :
            'isMandatoryForMainRegistrant';

        const formFieldsFlat = getFormFieldsForRender(formFields, fields).flat();

        for (let field of formFieldsFlat) {
            if ((isSecondaryRegistrant && !field[isMandatoryCheck]) || ignoreFormFieldCallback(field)) continue;

            if (field[isMandatoryCheck] && !fields[field.id]) {
                _errors[field.id] = field.noInputMessage || `Please provide ${field.label.toLowerCase()}`;
            }

            if (isSubmit && field.type === 'checkbox-multiple' && field.validationRegex) {
                const minAndMaxArray = field.validationRegex.split('-');
                const min = parseInt(minAndMaxArray[0]);
                const max = parseInt(minAndMaxArray[1]);
                const areMinAndMaxInvalid = isNaN(min) || isNaN(max) || min > max;
                const numOptionsChosen = cloneDeep(fields[field.id])?.filter((f: any) => f.value).length;
                const isError = !areMinAndMaxInvalid && (numOptionsChosen < min || numOptionsChosen > max);

                if (isError) {
                    _errors[field.id] = field.validationMessage;
                }
            } else if (isSubmit && field.validationRegex && !(new RegExp(field.validationRegex).test(fields[field.id]))) {
                _errors[field.id] = field.validationMessage;
            } else if (isSubmit && field.type === 'email' && !EmailValidator.validate(fields[field.id])) {
                _errors[field.id] = 'Please enter a valid email';
            } else if (isSubmit && field.type === 'number' && isNaN(parseFloat(fields[field.id]))) {
                _errors[field.id] = 'Please enter a valid number';
            }
        };

        return _errors;
    }, [formFields, ignoreFormFieldCallback, getFormFieldsForRender]);

    const validator: any = useCallback((
        fields: FieldList,
        isSubmit: boolean = false,
        innerData: InnerData|null = null,
    ) => {
        if (innerData) {
            let errors: any = {};

            const e = validateFields(
                fields,
                errors,
                isSubmit,
                { isSecondaryRegistrant: true }
            );

            return e;
        } else {
            let errors: any = { additionalRegistrants: {}  };

            const fieldsWithoutRegistrants = cloneDeep(fields);
            delete fieldsWithoutRegistrants.additionalRegistrants;
            errors = validateFields(fieldsWithoutRegistrants, errors, isSubmit);

            if (!!fields.additionalRegistrants) {
                Object.entries(fields.additionalRegistrants)?.forEach(([index, registrantFields]) => {
                    const e = validateFields(
                        registrantFields as any,
                        errors.additionalRegistrants[index] ?? {},
                        isSubmit,
                        { isSecondaryRegistrant: true }
                    );

                    errors.additionalRegistrants[index] = e;
                });
            }

            if (
                isEmpty(errors.additionalRegistrants) ||
                Object.values(errors.additionalRegistrants).every((ar: any) => isEmpty(ar))
            ) {
                delete errors.additionalRegistrants;
            }

            return errors;
        }
    }, [validateFields]);

    const {
        fields: registrationFields,
        errors: registrationErrors,
        onFieldChange,
        onReset,
        validate,
        setFields,
        setFormErrors,
        clearFormField,
        isValid,
        isFormChanged,
    } = useForm({
        fields,
        validator,
        validateOnChange: true,
        shouldScrollToError: true,
    });

    const addToCart = useCallback(async (
        data: EventPage,
        productQuantity: number,
        registrationId: string,
        userCode: string
    ) => {
        const response = await updateCart({
            productId: parseInt(data.id),
            productQuantity,
            registrationId,
            userCode,
            isEvent: true,
            // if event is online, then a customer should pay the price they are currently in
            countryCode: data.isOnline ? appState.ipCountry?.code : data.countryCode.code,
        });

        if (!response) {
            errorToast('There was an error when adding item to cart');
            return null;
        }

        return response;
    }, [updateCart, appState.ipCountry?.code, errorToast]);

    const onSubmit = useCallback( async () => {
        if (validate()) {
            setIsSubmitting(true);

            const variables: {
                eventRegistrationPageId: string|number,
                fields: string,
                editExistingWithId?: string|null,
                userCode?: string,
            } = {
                eventRegistrationPageId: registrationPageData.id,
                fields: JSON.stringify(registrationFields),
                editExistingWithId,
            };

            if (!!userCode) variables.userCode = userCode;

            return submitEventRegistration({ variables })
                .then((response) => {
                    const { data } = response;

                    const registration = data.submitEventRegistration;

                    if (!localStorage.getItem('userCode')) {
                        localStorage.setItem('userCode', registration.userCode);
                    }

                    navigate(confirmTo);

                    return response;
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        }
    }, [
        registrationFields,
        submitEventRegistration,
        validate,
        userCode,
        navigate,
        registrationPageData.id,
        confirmTo,
        editExistingWithId,
    ]);

    useEffect(() => {
        if (isEqual(prevRegistrantsData.current, initialRegistrantsData)) return;

        const essentialRegistrantFields = [
            { key: 'firstName', code: 'firstname' },
            { key: 'lastName', code: 'lastname'},
            { key: 'email', code: 'email' },
        ];

        prevRegistrantsData.current = initialRegistrantsData;

        if (isEmpty(initialRegistrantsData)) {
            setFields({ additionalRegistrants: {} });
        } else {
            initialRegistrantsData.forEach((r: any, i: number) => {
                const isMainRegistrant = i === 0;
                essentialRegistrantFields.forEach((essField) => {
                    if (has(r, essField.key) && !!r[essField.key]) {
                        const fieldId = formFields.find((f: FormField) => f.code === essField.code)?.id;

                        if (fieldId) {
                            onFieldChange(
                                fieldId,
                                r[essField.key],
                                isMainRegistrant ? { doNotValidate: true } : {
                                    innerData: {
                                        fieldId: 'additionalRegistrants',
                                        index: i - 1,
                                    },
                                    doNotValidate: true,
                                },
                            );

                            return;
                        }
                    }
                });

                const customFields = r.customFields;

                customFields.forEach((customField: CustomField) => {
                    let value: any = null;

                    if (customField.type === 'checkbox-multiple') {
                        const formField = allFormFields.find((f: FormField) => f.id === customField.formFieldId);

                        if (formField) {
                            value = formField.options?.map((op: EventRegistrationOption) => ({
                                id: op.id,
                                name: op.id,
                                label: op.label,
                                code: op.code,
                                value: customField.optionsChosen.some((o: OptionChosen) => o.id === op.id),
                            }));
                        }
                    } else if (customField.type === 'radio' || customField.type === 'dropdown') {
                        value = {};

                        if (customField.optionsChosen.length) {
                            value.id = customField.optionsChosen[0].id;
                            value.value = customField.optionsChosen[0].label;
                            value.code = customField.optionsChosen[0].code;
                        }
                    } else if (customField.type === 'checkbox') {
                        value = !!parseInt(customField.value);
                    } else {
                        value = customField.value;
                    }

                    onFieldChange(
                        customField.formFieldId,
                        value,
                        isMainRegistrant ? { doNotValidate: true } : {
                            innerData: {
                                fieldId: 'additionalRegistrants',
                                index: i - 1,
                            },
                        }
                    );
                });
            });
        }
    }, [
        allFormFields, initialRegistrantsData, onFieldChange, setFields, fields, onReset, formFields,
    ]);

    const formFieldsForRender = useMemo(() => (
        getFormFieldsForRender(formFields, registrationFields)
    ), [formFields, registrationFields, getFormFieldsForRender]);

    // Clear any fields that no longer are part of the form
    useEffect(() => {
        Object.entries(registrationFields).forEach(([id, value]: [string, any]) => {
            if (!registrationFields[id]) return;

            if (id === 'additionalRegistrants') {
                Object.entries(value).forEach(([rIndex, rFields]: [string, any]) => {
                    const additionalRegistrantFormFields = formFields.filter((f:FormField) => f.isMandatoryForSecondaryRegistrant);
                    const formFieldsFlat = getFormFieldsForRender(additionalRegistrantFormFields, rFields).flat();

                    Object.keys(rFields).forEach((rFieldId) => {
                        if (!formFieldsFlat.some((ff: FormField) => ff.id === rFieldId)) {
                            clearFormField(
                                rFieldId,
                                { innerData: { fieldId: 'additionalRegistrants', index: rIndex } }
                            );
                        }
                    });
                });

                return;
            } else if (!formFieldsForRender.flat().some((ff: FormField) => ff.id === id)) {
                clearFormField(id);
            }
        });
    }, [registrationFields, clearFormField, formFieldsForRender, getFormFieldsForRender, formFields]);

    return {
        registrationFields,
        registrationErrors,
        formFieldsForRender,
        getFormFieldsForRender,
        onFieldChange,
        addToCart,
        onSubmit,
        onReset,
        validate,
        setFormErrors,
        clearFormField,
        isValid,
        isFormChanged,
        isSubmitting,
    };
};

export default useEventRegistrationForm;
